import type { Cue, CueName } from './cues'
import { CUES } from './cues'

/**
 * A small sample player for interface sound.
 *
 * ## The graph
 *
 *     cue -> gain -> master -> limiter -> destination
 *
 * The limiter is a `DynamicsCompressor` with a hard ratio. Cues are fired by
 * user input and can overlap arbitrarily — a hover, a click and a page
 * transition can all land inside the same 100ms — and without it those sum
 * past full scale and clip. It costs one node and makes the kit unbreakable.
 *
 * ## Two-stage loading
 *
 * `prefetch()` downloads the encoded bytes; `unlock()` creates the audio
 * context and decodes them. They are separate because fetching is allowed at
 * any time while an `AudioContext` is not useful before a user gesture — and
 * because the title cue has to start the instant the gesture arrives, which
 * it cannot do if that is also when the download starts.
 *
 * Raw bytes are kept rather than decoded audio: `decodeAudioData` detaches
 * the `ArrayBuffer` it is given, so each decode works from a copy and the
 * originals survive a context being disposed and rebuilt.
 */

/** Hard cap on simultaneous voices. Beyond this, new cues are dropped. */
const MAX_VOICES = 12

/** Scheduling lookahead. Starting exactly at `currentTime` risks a glitch. */
const LOOKAHEAD = 0.015

const BASE_PATH = `${import.meta.env.BASE_URL}sfx/`

type AudioContextCtor = typeof AudioContext

const getAudioContextCtor = (): AudioContextCtor | undefined => {
  if (typeof window === 'undefined') return undefined
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor })
      .webkitAudioContext
  )
}

export const isAudioSupported = (): boolean =>
  getAudioContextCtor() !== undefined

export interface PlayResult {
  /** `performance.now()` at which the sound begins. */
  startsAt: number
}

export interface SoundEngine {
  /** Downloads the encoded sounds. Safe to call before any gesture. */
  prefetch: () => Promise<void>
  /**
   * Fetches and decodes one cue, resolving when it is playable.
   *
   * Exists so the title cue does not have to queue behind the other five.
   * It is twenty times their combined size, and the intro scene holds a dark
   * frame until it sounds — every millisecond spent decoding a hover tick
   * first is a millisecond of black screen.
   */
  load: (name: CueName) => Promise<boolean>
  /**
   * Creates (or resumes) the audio graph. Normally run inside a user
   * gesture.
   *
   * `timeout` caps how long to wait for the resume to take effect, because
   * Chromium leaves the promise pending rather than rejecting when the page
   * has no user activation. Pass a short one for a speculative attempt where
   * a fast "no" matters more than a certain answer.
   */
  unlock: (timeout?: number) => Promise<boolean>
  /**
   * Schedules a cue, returning when it will actually sound so callers can
   * line visuals up with it. `null` when nothing was played.
   */
  play: (name: CueName) => PlayResult | null
  setMuted: (muted: boolean) => void
  setVolume: (volume: number) => void
  /** Frees the audio thread. Downloaded bytes are kept. */
  dispose: () => void
}

export const createSoundEngine = (): SoundEngine => {
  let ctx: AudioContext | undefined
  let master: GainNode | undefined

  const encoded = new Map<CueName, ArrayBuffer>()
  const decoded = new Map<CueName, AudioBuffer>()
  const fetches = new Map<CueName, Promise<void>>()
  let fetching: Promise<void> | undefined

  let muted = false
  let volume = 0.8
  let voices = 0
  const lastPlayed = new Map<CueName, number>()

  const names = Object.keys(CUES) as CueName[]

  const fetchOne = (name: CueName) => {
    const existing = fetches.get(name)
    if (existing) return existing

    const job = (async () => {
      try {
        const response = await fetch(`${BASE_PATH}${CUES[name].src}`)
        if (!response.ok) return
        encoded.set(name, await response.arrayBuffer())
      } catch {
        // A missing sound is a missing sound. The site is unaffected.
      }
    })()

    fetches.set(name, job)
    return job
  }

  const prefetch = async () => {
    fetching ??= Promise.all(names.map(fetchOne)).then(() => undefined)
    return fetching
  }

  const load = async (name: CueName): Promise<boolean> => {
    if (decoded.has(name)) return true

    await fetchOne(name)
    const bytes = encoded.get(name)
    if (!bytes || !ctx) return false

    try {
      // `slice()` because decoding detaches the buffer it is handed.
      decoded.set(name, await ctx.decodeAudioData(bytes.slice(0)))
      return true
    } catch {
      // Undecodable on this browser; that cue simply stays silent.
      return false
    }
  }

  const build = (): boolean => {
    const Ctor = getAudioContextCtor()
    if (!Ctor) return false

    const context = new Ctor()

    const limiter = context.createDynamicsCompressor()
    limiter.threshold.value = -6
    limiter.knee.value = 0
    limiter.ratio.value = 20
    limiter.attack.value = 0.003
    limiter.release.value = 0.12
    limiter.connect(context.destination)

    const masterGain = context.createGain()
    masterGain.gain.value = muted ? 0.0001 : volume
    masterGain.connect(limiter)

    ctx = context
    master = masterGain
    return true
  }

  const unlock = async (timeout = 4000): Promise<boolean> => {
    if (!ctx && !build()) return false
    const context = ctx
    if (!context) return false

    // Safari can hand back a context that is still suspended even after a
    // gesture, so resume explicitly rather than trusting the initial state.
    if (context.state === 'suspended') {
      // Chromium does **not** reject `resume()` when the page lacks user
      // activation — it leaves the promise pending, indefinitely, until a
      // gesture eventually arrives. Awaiting it therefore never returns a
      // "no", and anything waiting on the answer waits forever. So race it
      // against a deadline and read the state instead.
      const resumed = context.resume().catch(() => undefined)
      await Promise.race([
        resumed,
        new Promise((resolve) => window.setTimeout(resolve, timeout)),
      ])
    }
    if (context.state !== 'running') return false

    // Decode in the background. Callers that need a specific cue *now* —
    // the intro scene does — await `load()` for that one instead of blocking
    // on the whole set.
    void prefetch().then(() => Promise.all(names.map(load)))
    return true
  }

  const play = (name: CueName): PlayResult | null => {
    if (muted || !ctx || !master || ctx.state !== 'running') return null

    const buffer = decoded.get(name)
    if (!buffer) return null

    const cue: Cue = CUES[name]
    const now = performance.now()

    // Throttle. Hover in particular fires on every pixel of pointer travel
    // across a dense list.
    const throttle = cue.throttle ?? 0
    if (throttle > 0) {
      const last = lastPlayed.get(name) ?? 0
      if (now - last < throttle) return null
      lastPlayed.set(name, now)
    }

    if (voices >= MAX_VOICES) return null

    const source = ctx.createBufferSource()
    source.buffer = buffer

    const gain = ctx.createGain()
    gain.gain.value = cue.gain
    source.connect(gain)
    gain.connect(master)

    const start = ctx.currentTime + LOOKAHEAD
    source.start(start)

    voices += 1
    source.onended = () => {
      voices = Math.max(0, voices - 1)
      source.disconnect()
      gain.disconnect()
    }

    return { startsAt: now + LOOKAHEAD * 1000 }
  }

  const setMuted = (next: boolean) => {
    muted = next
    if (!ctx || !master) return

    // Ramp rather than jump: an instant gain change on a sounding voice is
    // itself an audible click.
    const t = ctx.currentTime
    master.gain.cancelScheduledValues(t)
    master.gain.setValueAtTime(master.gain.value, t)
    master.gain.linearRampToValueAtTime(next ? 0.0001 : volume, t + 0.08)
  }

  const setVolume = (next: number) => {
    volume = Math.min(1, Math.max(0, next))
    if (!muted) setMuted(false)
  }

  const dispose = () => {
    lastPlayed.clear()
    decoded.clear()
    voices = 0
    void ctx?.close().catch(() => undefined)
    ctx = undefined
    master = undefined
  }

  return { prefetch, load, unlock, play, setMuted, setVolume, dispose }
}
